import { MouseEventHandler } from 'react';
import {
    AppBar as MuiAppBar,
    Toolbar,
    IconButton,
    Typography,
    styled,
    Box,
    AppBarProps as MuiAppBarProps
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../config/ApiService';
import { useDispatch, useSelector } from 'react-redux';
import LogoutIcon from '@mui/icons-material/ExitToApp';
import AuthActions from 'redux/actions/AuthActions';
import AuthActionCreators from 'redux/creators/AuthActionCreators';
import CurrentUserNavBarItem from './CurrentUserNavBarItem';
import { LOG_TYPES } from 'utils/Logger';
import ClinicalServicesDropdown from './ClinicalServicesDropdown';
import { RootState } from 'redux/reducers';
// import { useLocation } from 'react-router-dom';
import RegionFlagComponent from './RegionFlagComponent';
import { useEngageStore } from 'pages/Engage/state';
import VersionComponent from './VersionComponent';
import OrgClientDropdown from './OrgClientDropdown';
import { setUserActivity } from 'services/trackUserActivity';

interface AppBarProps extends MuiAppBarProps {
    open?: boolean;
}

const drawerWidth = 240;

interface TopBarProps {
    open: boolean;
    onDrawerOpen: MouseEventHandler;
}

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open'
})<AppBarProps>(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen
    }),
    ...(open && {
        width: `calc(100% - ${drawerWidth}px)`,
        marginLeft: drawerWidth,
        transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen
        })
    })
}));

const TopBar = (props: TopBarProps) => {
    const { open, onDrawerOpen } = props;
    const clinician = useSelector((state: RootState) => state.AuthReducer.user);
    const acl = useSelector((state: RootState) => state.AuthReducer.acl);
    const permissions = useSelector((state: RootState) => state.AuthReducer.permissions);

    const region = useSelector((state: RootState) => state.AuthReducer.currentRegion);

    const { resetFilters } = useEngageStore();

    const dispatch = useDispatch();

    const location = useLocation();
    const { pathname } = location;

    const isInternalClinicalServices = permissions.includes('internal_clinical_services');

    const showClinicalServicesDropdown = () => {
        if (pathname.match('phioengage') && isInternalClinicalServices) {
            return false;
        }
        return isInternalClinicalServices;
    };

    const showOrgClientDropdown = () => {
        return !showClinicalServicesDropdown() && acl?.role === 'org-admin';
    };

    const handleLogout = async () => {
        try {
            await setUserActivity({
                eventName: 'logged_off'
            });
            dispatch(
                AuthActionCreators.logForAudit({
                    target: {
                        id: clinician.id,
                        description: 'Logged off platform',
                        affectedUser: clinician.email
                    },
                    type: LOG_TYPES.READ
                })
            );
            // reset filters in engage store
            resetFilters();
            signOut(auth);
            dispatch({ type: AuthActions.SIGN_OUT });
        } catch (e: unknown) {
            if (e instanceof Error) {
                console.log(e.message);
            } else {
                console.log('An unknown error occurred:', e);
            }
        }
    };

    return (
        <AppBar position="fixed" open={open}>
            <Toolbar>
                <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    onClick={onDrawerOpen}
                    edge="start"
                    sx={{ marginRight: 5, ...(open && { display: 'none' }) }}
                >
                    <MenuIcon />
                </IconButton>
                <Typography component="h1" variant="h6" color="inherit" noWrap>
                    Phio Portal
                </Typography>
                <VersionComponent />
                <Box sx={{ flexGrow: 1 }} />
                {showOrgClientDropdown() && <OrgClientDropdown />}
                {showClinicalServicesDropdown() && <ClinicalServicesDropdown />}
                <RegionFlagComponent region={region} />
                <CurrentUserNavBarItem />
                <IconButton onClick={handleLogout} color="inherit">
                    <LogoutIcon />
                </IconButton>
            </Toolbar>
        </AppBar>
    );
};

export default TopBar;
